<template>
  <div>
    <v-dialog width="600" v-model="showDialog">
      <template v-slot:activator="{ attrs }">
        <v-btn
          v-if="minifiedIcon"
          outlined
          icon
          color="primary"
          @click="loadPopupDialog"
        >
          <eva-icon :fill="$color.coro.purple" name="edit-outline" />
        </v-btn>
        <v-btn
          x-large
          text
          v-bind="{ ...attrs, ...extraProps }"
          @click="loadPopupDialog"
        >
          {{ label ? label : $ucfirst($t("patient.openrecord")) }}
        </v-btn>
      </template>

      <v-card>
        <div class="clearfix">
          <v-btn
            class="pull-right btn-transparent"
            icon
            plain
            @click="showDialog = false"
          >
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </div>
        <div class="pa-5">
          <div class="d-flex justify-center pb-5">
            <v-icon class="mr-2"> mdi-help </v-icon>
            {{ $ucfirst($t("patient.whyopenrecord")) }}
          </div>
          <div
            v-if="remainingQuestionnaireKeys.length > 0"
            class="d-flex justify-center pb-5 color-coro-warning-500"
          >
            <v-icon :color="$color.coro['warning-500']" class="mr-2">
              mdi-alert
            </v-icon>
            {{ $ucfirst($t("patient.noinfoquestionnaire")) }}
          </div>
          <div class="d-flex justify-space-around pb-5 flex-wrap">
            <v-btn
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              @click="openRecordForFollowUp"
              class="mt-2"
            >
              {{ $ucfirst($t("patient.followup")) }}
            </v-btn>
            <v-btn
              v-if="isSDMButtonAvailable"
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              @click="openRecordForSDM"
              :disabled="hasReadOnlyAccess"
              class="mt-2"
            >
              {{
                $ucfirst(
                  $t("patient.startvisit", {
                    params: { visitNumber: currentVersion },
                  })
                )
              }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Enter study id dialog -->
    <v-dialog v-model="showStudyIdDialog" max-width="700px">
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{
            $ucfirst(
              $t("patient.enterSubjectIdToStartVisit", {
                params: { visitNumber: currentVersion },
              })
            )
          }}
        </div>
        <v-text-field
          class="mt-6"
          v-model="patientStudyId"
          dense
          :error="!!errorMessage"
          :error-messages="errorMessage"
          :placeholder="
            $ucfirst(
              $t('pdf.medicationPrescription.subjectId', {
                catalog: 'messages',
              })
            )
          "
          ref="dialogInput"
        >
        </v-text-field>
        <div class="dashboard-dialog-window__actions mt-6">
          <v-btn x-large text class="ml-4" @click="openRecordForSDM">
            {{
              $ucfirst(
                $t("patient.startvisit", {
                  params: { visitNumber: currentVersion },
                })
              )
            }}
          </v-btn>
          <v-btn x-large text class="ml-4" @click="closeDialog" color="primary">
            {{ $ucfirst($t("buttons.cancel", { catalog: "app_mobile" })) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

/**
 * Component OpenPatientRecordButton.vue
 *
 * @module OpenPatientRecordButton
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/components/Patient/OpenPatientRecordButton.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "OpenPatientRecordButton",
  props: {
    minifiedIcon: {
      type: Boolean,
      default: () => false,
    },
    askConfirmation: {
      type: Boolean,
      default: () => true,
    },
    label: {
      type: String,
    },
    extraProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showDialog: false,
      remainingQuestionnaireKeys: [],
      showStudyIdDialog: false,
      errorMessage: "",
      patientStudyId: "",
    };
  },
  computed: {
    ...mapGetters({
      currentVersion: "patientSummary/currentVersion",
      patientId: "patientSummary/id",
      subjectId: "patientSummary/studyId",
      lastVersion: "patientSummary/lastVersion",
      isSDMStarted: "followupSession/isSDMStarted",
      isFollowUpStarted: "followupSession/isFollowUpStarted",
      getEncounter: "patientSummary/getEncounter",
      hasReadOnlyAccess: "user/hasReadOnlyAccess",
    }),
    isSDMButtonAvailable() {
      return (
        this.currentVersion &&
        !this.isSDMStarted &&
        !this.isFollowUpStarted &&
        this.lastVersion &&
        !(this.getEncounter(7) && this.currentVersion === 7)
      );
    },
  },
  watch: {
    showStudyIdDialog(curr) {
      if (curr) {
        setTimeout(() => {
          this.$refs.dialogInput.focus();
        }, 200);
      }
    },
    showDialog(curr) {
      if (curr) {
        if (!this.askConfirmation) {
          this.openRecordForSDM();
        }
      }
    },
  },
  methods: {
    async openRecordForFollowUp() {
      try {
        await this.$store.dispatch("followupSession/startRegular");

        this.$router.push({ name: "PatientRecordProgressStatusJourney" });
        this.$toasted.show(
          this.$t("dummy.SDM.new_followup_started", {
            params: {
              patientName: this.$store.getters["patientSummary/studyId"],
            },
          })
        );
      } catch (error) {
        return;
      }
    },
    startEncounter() {
      this.$router.push({
        name: "PatientEncounterVitalSigns",
        params: { version: this.currentVersion },
      });
    },
    async loadPopupDialog() {
      const collectionKey = "BEFORE_" + this.currentVersion,
        qManager = this.$app.questionnaireManager;

      const collection = await qManager.getEProConfigurationCollection(
        this.subjectId,
        collectionKey
      );

      this.remainingQuestionnaireKeys = collection.remainingQuestionnaireKeys;

      this.showDialog = true;
    },
    openRecordForSDM() {
      if (this.showStudyIdDialog === true) {
        if (!this.patientStudyId) {
          this.errorMessage = this.$ucfirst(this.$t("validation.IS_REQUIRED"));
        } else if (
          this.patientStudyId.toLowerCase() !== this.subjectId.toLowerCase()
        ) {
          this.errorMessage = "";
          this.$toasted.show(
            this.$ucfirst(this.$t("validation.subjectIdDoesntCorrespond")) +
              " " +
              this.$ucfirst(this.$t("validation.enterCorrectIdToStartVisit")),
            {
              type: "error",
              position: "bottom-center",
              duration: 6000,
            }
          );
        } else {
          this.startEncounter();
          this.closeDialog();
        }
      } else {
        this.showDialog = false;
        this.showStudyIdDialog = true;
      }
    },

    closeDialog() {
      this.showStudyIdDialog = false;
      this.errorMessage = "";
      this.patientStudyId = "";
    },
  },
};
</script>

<style lang="scss">
// mobile first

// mobile screens

// tablet screens

// desktop/large screens

// -------------------------------------------------- BEM modifiers ------------------------------------------------
</style>
